import {
  CarrierType,
  CarrierVisitStatus,
  CarrierVisitWithWorkQueueDto,
  EquipmentDto,
  EquipmentType,
  OperationType,
  WorkInstructionControlRoomDto,
  WorkInstructionStatus,
  WorkInstructionTotalNumbersDto,
} from '@operations/app/api'
import { CarrierVisitRefreshEventPayload } from '@operations/app/api/signalRDtos/carrierVisitRefreshEventPayload'
import { CarrierVisitWorkInstructionUpsertedPayload } from '@operations/app/api/signalRDtos/carrierVisitWorkInstructionUpsertedPayload'
import { JobUpsertedUpdatedEventPayload } from '@operations/app/api/signalRDtos/JobUpsertedEventPayload'
import { WorkInstructionDeletedEventPayload } from '@operations/app/api/signalRDtos/workInstructionDeletedEventPayload'
import { WorkInstructionUpsertedEventPayload } from '@operations/app/api/signalRDtos/workInstructionUpsertedEventPayload'
import { WorkQueueUpsertedEventPayload } from '@operations/app/api/signalRDtos/workQueueUpsertedEventPayload'
import { JobSignalRDto } from '@operations/features/equipmentOperator/models/job-signalR-event.model'
import { EventTypes } from '@operations/messages/eventsTypes'
import { workInstructionService } from '@operations/services'
import carrierVisitService from '@operations/services/carrierVisitService'
import { workQueueService } from '@operations/services/workQueueService'
import { carrierVisitOnTerminalStatus } from '@operations/utils/helper'
import { HousekeepingPlanSummaryResponse, housekeepingPlanService } from '@tom-ui/yard'
import { appStore, IEvent, MessageBus } from '@tom-ui/utils'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import {
  buildHousekeepingGroups,
  hasUngroupedInternalWorkInstructions,
  uniqueHousekeepingPlanIds,
} from '../helpers/housekeeping-group.helper'
import {
  isCarrierTypeMatchingTab,
  mapCarrierVisitCraneToViewModel,
  mapCarrierVisitWorkQueueToViewModel,
  mapWorkInstructionToViewModel,
  mapWorkInstructionUpsertedEventToViewModel,
  mapWorkTabTypeToCarrierType,
  sortCarrierVisitsByStatusAndName,
} from '../helpers/work-instruction-table.mapper'
import {
  CarrierVisitViewModel,
  CarrierVisitWorkQueueViewModel,
  ControlRoomWorkTabType,
  HousekeepingGroupViewModel,
  OTHERS_GROUP_ID,
  WorkInstructionViewModel,
} from '../models/work-instruction-view.model'

const carrierVisitStatusToFetch = [...carrierVisitOnTerminalStatus, CarrierVisitStatus.Departed]

export class WorkInstructionUIStore {
  private isWorkInstructionsLoading = false
  private isCarrierVisitsLoading = false
  private isCountLoading = false

  loadingSignalR = false
  signalREventsQueue: JobSignalRDto[] = []

  isNameWorkQueueDialogOpen = false
  isStartWorkQueueDialogOpen = false
  isStopWorkQueueDialogOpen = false

  isAmendHousekeepingPlanDialogOpen = false
  isCancelHousekeepingPlanDialogOpen = false

  carrierVisitsWithWorkQueues: CarrierVisitWithWorkQueueDto[] = []
  selectedTabType: ControlRoomWorkTabType = 'vessel'
  selectedVisitId?: number = undefined
  selectedCraneId?: number = undefined
  selectedWorkQueueId?: number = undefined
  selectedWorkQueueForAction?: CarrierVisitWorkQueueViewModel = undefined

  workInstructions: WorkInstructionControlRoomDto[] = []

  workInstructionTotalNumbers: WorkInstructionTotalNumbersDto[] = []

  housekeepingPlanSummaries: HousekeepingPlanSummaryResponse[] = []
  selectedHousekeepingGroupId?: string = undefined
  // Label injected from the React layer where Tolgee's useTranslate is available.
  // Defaults match the i18n fallback so unit tests don't need a translator.
  othersGroupLabel = 'Ad-hoc moves'
  private isHousekeepingPlansLoading = false

  constructor(private readonly messageBus: MessageBus) {
    makeObservable(this, {
      workInstructions: observable,
      workInstructionTotalNumbers: observable,
      carrierVisitsWithWorkQueues: observable,
      selectedTabType: observable,
      selectedCraneId: observable,
      selectedVisitId: observable,
      selectedWorkQueueId: observable,
      selectedWorkQueueForAction: observable,
      housekeepingPlanSummaries: observable,
      selectedHousekeepingGroupId: observable,
      othersGroupLabel: observable,
      isNameWorkQueueDialogOpen: observable,
      isStartWorkQueueDialogOpen: observable,
      isStopWorkQueueDialogOpen: observable,
      isAmendHousekeepingPlanDialogOpen: observable,
      isCancelHousekeepingPlanDialogOpen: observable,

      setWorkInstructionTotalNumbers: action,
      setSelectedTabType: action,
      setSelectedCraneId: action,
      setSelectedWorkQueueId: action,
      setSelectedWorkQueueForAction: action,
      setSelectedVisitId: action,
      setSelectedHousekeepingGroupId: action,
      setOthersGroupLabel: action,
      setIsNameWorkQueueDialogOpen: action,
      toggleStartWorkQueueDialog: action,
      toggleStopWorkQueueDialog: action,
      openAmendHousekeepingPlanDialog: action,
      closeAmendHousekeepingPlanDialog: action,
      openCancelHousekeepingPlanDialog: action,
      closeCancelHousekeepingPlanDialog: action,

      filteredCarrierVisits: computed,
      filteredWorkInstructions: computed,
      selectedHousekeepingGroup: computed,
      isHousekeepingPlanSelected: computed,
      pendingHousekeepingMovesCount: computed,
      vesselDischargeTotal: computed,
      vesselLoadTotal: computed,
      truckDischargeTotal: computed,
      truckLoadTotal: computed,
      hasSidePanel: computed,
      hasHousekeepingPanel: computed,
      housekeepingGroups: computed,
      workQueues: computed,
      canStartOrStop: computed,
      hasTableActions: computed,
    })

    this.messageBus.subscribeEvent<WorkInstructionUpsertedEventPayload>(
      EventTypes.WorkInstructionUpserted,
      message => this.enqueueSignalREvent(message, this.handleWorkInstructionUpsertedEvent),
    )

    this.messageBus.subscribeEvent<JobUpsertedUpdatedEventPayload>(
      EventTypes.JobUpserted,
      message => this.enqueueSignalREvent(message, this.handleJobUpsertedEvent),
    )

    this.messageBus.subscribeEvent<number>(EventTypes.WorkInstructionFinished, message =>
      this.enqueueSignalREvent(message, this.handleWorkInstructionFinishedEvent),
    )

    this.messageBus.subscribeEvent<WorkInstructionDeletedEventPayload>(
      EventTypes.WorkInstructionDeleted,
      message => this.enqueueSignalREvent(message, this.handleWorkInstructionDeletedEvent),
    )

    this.messageBus.subscribeEvent<EquipmentDto>(EventTypes.EquipmentUpserted, message =>
      this.enqueueSignalREvent(message, this.handleEquipmentUpsertedEvent),
    )

    this.messageBus.subscribeEvent<CarrierVisitRefreshEventPayload>(
      EventTypes.CarrierVisitRefresh,
      message => this.enqueueSignalREvent(message, this.handleCarrierVisitRefreshEvent),
    )

    this.messageBus.subscribeEvent<WorkQueueUpsertedEventPayload>(
      EventTypes.WorkQueueUpserted,
      message => this.enqueueSignalREvent(message, this.upsertWorkQueueBySignalR),
    )

    this.messageBus.subscribeEvent<number>(EventTypes.CraneSplitGeneratedForVisit, message =>
      this.enqueueSignalREvent(message, this.handleCraneSplitGeneratedForVisitEvent),
    )

    this.messageBus.subscribeEvent(EventTypes.CarrierVisitWorkInstructionUpserted, message =>
      this.enqueueSignalREvent(message, this.handleCarrierVisitWorkInstructionUpsertedEvent),
    )
  }

  public async fetchWorkInstructions(workInstructionIds: number[] = []) {
    const visits = this.selectedVisitId ? [this.selectedVisitId] : []
    const carrierVisitType = mapWorkTabTypeToCarrierType(this.selectedTabType)

    const workInstructions = await workInstructionService.getControlRoomWorkInstructions(
      visits,
      carrierVisitType,
      workInstructionIds,
    )

    return workInstructions
  }

  public async fetchAndSetWorkInstructions() {
    if (this.isWorkInstructionsLoading) return

    this.isWorkInstructionsLoading = true
    const workInstructions = await this.fetchWorkInstructions().finally(
      () => (this.isWorkInstructionsLoading = false),
    )

    if (!workInstructions) return

    runInAction(() => {
      this.workInstructions = workInstructions
    })

    // Plan summaries depend on the freshly-loaded WI list (we read `housekeepingPlanId` off it
    // to bound the yard fetch). Chaining here — rather than at every caller — avoids a race
    // where setSelectedTabType('internal') fires the summary fetch before the WI fetch lands.
    if (this.hasHousekeepingPanel) {
      await this.fetchAndSetHousekeepingPlanSummaries()
    }
  }

  public async fetchWorkInstructionTotalNumbers() {
    if (this.isCountLoading) return

    this.isCountLoading = true

    const workInstructionTotalNumbers = await workInstructionService
      .getTotalNumbers()
      .finally(() => {
        this.isCountLoading = false
      })
    this.setWorkInstructionTotalNumbers(workInstructionTotalNumbers)
  }

  public async fetchCarrierVisits(ids: number[] = []) {
    if (this.isCarrierVisitsLoading) return
    this.isCarrierVisitsLoading = true

    const carrierVisits = await carrierVisitService
      .getCarrierVisitsWithWorkQueues(carrierVisitStatusToFetch, ids)
      .finally(() => (this.isCarrierVisitsLoading = false))

    return carrierVisits
  }

  public async fetchAndSetCarrierVisits() {
    const carrierVisits = await this.fetchCarrierVisits()
    if (!carrierVisits) return

    runInAction(() => {
      this.carrierVisitsWithWorkQueues = carrierVisits
    })
  }

  public async fetchAndSetHousekeepingPlanSummaries() {
    if (this.isHousekeepingPlansLoading) return

    const ids = uniqueHousekeepingPlanIds(this.workInstructions)
    if (ids.length === 0) {
      runInAction(() => {
        this.housekeepingPlanSummaries = []
      })
      return
    }

    this.isHousekeepingPlansLoading = true
    try {
      const summaries = await housekeepingPlanService.getPlans(ids)
      runInAction(() => {
        this.housekeepingPlanSummaries = summaries
      })
    } finally {
      this.isHousekeepingPlansLoading = false
    }
  }

  public setWorkInstructionTotalNumbers(
    workInstructionTotalNumbers: WorkInstructionTotalNumbersDto[],
  ) {
    this.workInstructionTotalNumbers = workInstructionTotalNumbers
  }

  public setSelectedTabType(tabType: ControlRoomWorkTabType) {
    if (this.selectedTabType !== tabType) {
      this.selectedTabType = tabType
      this.setFirstVisitIdForSelectedTab()
      this.fetchAndSetWorkInstructions()
    }
  }

  public setSelectedHousekeepingGroupId(groupId?: string) {
    if (this.selectedHousekeepingGroupId !== groupId) {
      this.selectedHousekeepingGroupId = groupId
    }
  }

  public setOthersGroupLabel(label: string) {
    this.othersGroupLabel = label
  }

  public openAmendHousekeepingPlanDialog() {
    this.isAmendHousekeepingPlanDialogOpen = true
  }

  public closeAmendHousekeepingPlanDialog() {
    this.isAmendHousekeepingPlanDialogOpen = false
  }

  public openCancelHousekeepingPlanDialog() {
    this.isCancelHousekeepingPlanDialogOpen = true
  }

  public closeCancelHousekeepingPlanDialog() {
    this.isCancelHousekeepingPlanDialogOpen = false
  }

  // Cancels the currently selected housekeeping plan via the yard service.
  // Caller (the dialog) handles error toasting; we just throw on failure so the
  // dialog can keep itself open if the call rejects.
  public async cancelSelectedHousekeepingPlan(): Promise<void> {
    const planId = this.selectedHousekeepingGroupId
    if (!planId || planId === OTHERS_GROUP_ID) return
    await housekeepingPlanService.cancelPlan(planId)
    // The yard SignalR push + operations WI updates flow back through the
    // existing subscriptions; no manual refresh required.
  }

  public setSelectedCraneId(craneId?: number) {
    this.selectedCraneId = craneId
    this.setSelectedWorkQueueId()
  }

  public setSelectedWorkQueueId(id?: number) {
    if (this.selectedWorkQueueId !== id) this.selectedWorkQueueId = id
  }

  public setSelectedWorkQueueForAction(workQueue?: CarrierVisitWorkQueueViewModel) {
    if (this.selectedWorkQueueForAction !== workQueue) this.selectedWorkQueueForAction = workQueue
  }

  public async setSelectedVisitId(visitId?: number) {
    this.setSelectedCraneId()

    if (this.selectedVisitId !== visitId) {
      this.selectedVisitId = visitId

      await this.fetchAndSetWorkInstructions()
    }
  }

  public setIsNameWorkQueueDialogOpen(isOpen: boolean) {
    this.isNameWorkQueueDialogOpen = isOpen
  }

  public toggleStartWorkQueueDialog(workQueue?: CarrierVisitWorkQueueViewModel) {
    this.isStartWorkQueueDialogOpen = !this.isStartWorkQueueDialogOpen
    this.setSelectedWorkQueueForAction(workQueue)
  }

  public toggleStopWorkQueueDialog(workQueue?: CarrierVisitWorkQueueViewModel) {
    this.isStopWorkQueueDialogOpen = !this.isStopWorkQueueDialogOpen
    this.setSelectedWorkQueueForAction(workQueue)
  }

  public async setFirstVisitIdForSelectedTab(visitId?: number | null) {
    const firstVisit = this.filteredCarrierVisits[0]
    await this.setSelectedVisitId(visitId ?? firstVisit?.id)
  }

  public async stopWorkInstructions(workInstructions: WorkInstructionControlRoomDto[]) {
    await workInstructionService.stop(workInstructions.map(wi => wi.id))
  }

  public async startWorkInstruction(workInstructions: WorkInstructionControlRoomDto[]) {
    await workInstructionService.start(workInstructions.map(wi => wi.id))
  }

  public async setPriority(ids: number[], isPriority: boolean) {
    await workInstructionService.setPriority(ids, isPriority)
  }

  async updateWorkQueueName(id: number, workQueueName: string | null | undefined) {
    await workQueueService.rename(id, workQueueName)
  }

  get canStartOrStop() {
    return (
      this.selectedTabType === 'vessel' &&
      this.carrierVisit?.status === CarrierVisitStatus.InOperation
    )
  }

  get hasTableActions() {
    return (
      this.canStartOrStop ||
      this.filteredWorkInstructions.some(
        wi =>
          wi.status !== WorkInstructionStatus.Finished &&
          wi.status !== WorkInstructionStatus.WaitingForStackIn,
      )
    )
  }

  get vesselDischargeTotal() {
    return (
      this.workInstructionTotalNumbers.find(
        w => w.carrierVisitType === CarrierType.Vessel && w.operationType === OperationType.Inbound,
      )?.totalNumber ?? 0
    )
  }

  get vesselLoadTotal() {
    return (
      this.workInstructionTotalNumbers.find(
        w =>
          w.carrierVisitType === CarrierType.Vessel && w.operationType === OperationType.Outbound,
      )?.totalNumber ?? 0
    )
  }

  get truckDischargeTotal() {
    return (
      this.workInstructionTotalNumbers.find(
        w => w.carrierVisitType === CarrierType.Truck && w.operationType === OperationType.Inbound,
      )?.totalNumber ?? 0
    )
  }

  get truckLoadTotal() {
    return (
      this.workInstructionTotalNumbers.find(
        w => w.carrierVisitType === CarrierType.Truck && w.operationType === OperationType.Outbound,
      )?.totalNumber ?? 0
    )
  }

  get filteredCarrierVisits(): CarrierVisitViewModel[] {
    return sortCarrierVisitsByStatusAndName(
      this.carrierVisitsWithWorkQueues
        .filter(
          visit =>
            isCarrierTypeMatchingTab(visit.type, this.selectedTabType) &&
            (this.selectedVisitId === visit.id || carrierVisitStatusToFetch.includes(visit.status)),
        )
        .map(visit => {
          const newVisit: CarrierVisitViewModel = {
            ...visit,
            cranes: visit.cranes
              .filter(crane => crane.workQueues.length > 0)
              .map(crane => {
                const workQueues = crane.workQueues.map(wq =>
                  mapCarrierVisitWorkQueueToViewModel(wq, this.workInstructions),
                )

                return mapCarrierVisitCraneToViewModel(crane, workQueues)
              }),
          }

          return newVisit
        }),
    )
  }

  get carrierVisit(): CarrierVisitViewModel | undefined {
    return this.filteredCarrierVisits.find(v => v.id === this.selectedVisitId)
  }

  get workQueues() {
    const visit = this.carrierVisit
    if (!visit) return {}

    const workQueueDict: Record<
      number,
      { workQueue: CarrierVisitWorkQueueViewModel; craneName: string }
    > = {}

    visit.cranes.forEach(crane => {
      crane.workQueues.forEach(workQueue => {
        workQueueDict[workQueue.id] = {
          workQueue,
          craneName: crane.name,
        }
      })
    })

    return workQueueDict
  }

  get filteredWorkInstructions(): WorkInstructionViewModel[] {
    if (this.hasHousekeepingPanel) {
      if (!this.selectedHousekeepingGroupId) return this.workInstructions

      if (this.selectedHousekeepingGroupId === OTHERS_GROUP_ID) {
        return this.workInstructions.filter(wi => !wi.housekeepingPlanId)
      }
      return this.workInstructions.filter(
        wi => wi.housekeepingPlanId === this.selectedHousekeepingGroupId,
      )
    }

    if (!this.hasSidePanel) return this.workInstructions

    const workQueueIds: number[] = []

    if (this.selectedCraneId) {
      const crane = this.carrierVisit?.cranes.find(crane => crane.id === this.selectedCraneId)
      const workQueues = crane?.workQueues

      if (this.selectedWorkQueueId && workQueues?.some(wq => wq.id === this.selectedWorkQueueId)) {
        workQueueIds.push(this.selectedWorkQueueId)
      } else if (workQueues?.length) {
        workQueueIds.push(...workQueues.map(wq => wq.id))
      }
    }

    return this.workInstructions
      .filter(
        wi =>
          wi.carrierVisitId === this.selectedVisitId &&
          (!workQueueIds.length || (wi.workQueueId && workQueueIds.includes(wi.workQueueId))),
      )
      .map(wi => {
        if (!wi.workQueueId) return wi
        const workQueueDictionary = this.workQueues[wi.workQueueId]

        return mapWorkInstructionToViewModel(
          wi,
          workQueueDictionary?.workQueue,
          workQueueDictionary?.craneName,
        )
      })
  }

  get hasSidePanel() {
    const displayTabs = ['vessel', 'train', 'internal']
    return displayTabs.includes(this.selectedTabType)
  }

  get hasHousekeepingPanel() {
    return this.selectedTabType === 'internal'
  }

  get housekeepingGroups(): HousekeepingGroupViewModel[] {
    return buildHousekeepingGroups(
      this.housekeepingPlanSummaries,
      this.othersGroupLabel,
      hasUngroupedInternalWorkInstructions(this.workInstructions),
    )
  }

  // The view-model entry for the currently selected non-Others housekeeping group.
  // Returns undefined when the user has nothing selected, has the Others bucket
  // selected, or when the summary list hasn't loaded yet.
  get selectedHousekeepingGroup(): HousekeepingGroupViewModel | undefined {
    if (!this.selectedHousekeepingGroupId) return undefined
    return this.housekeepingGroups.find(
      g => !g.isOthers && g.id === this.selectedHousekeepingGroupId,
    )
  }

  get isHousekeepingPlanSelected(): boolean {
    return this.selectedHousekeepingGroup !== undefined
  }

  // Count of WIs for the selected plan that are still pickable (Status === Todo).
  // Used by the action menu to disable Amend/Cancel when nothing is pending and
  // by the cancel confirmation dialog to label its "X pending moves" copy.
  get pendingHousekeepingMovesCount(): number {
    if (!this.isHousekeepingPlanSelected) return 0
    return this.filteredWorkInstructions.filter(wi => wi.status === WorkInstructionStatus.Todo)
      .length
  }

  private handleWorkInstructionUpsertedEvent = async (
    res: IEvent<WorkInstructionUpsertedEventPayload>,
  ) => {
    if (!res.payload) return

    await appStore.triggerRequestWithoutLoader(async () => {
      let workInstructions: WorkInstructionViewModel[] = []
      let hasUpdatedWorkInstruction = false
      const workInstructionIndex = this.workInstructions.findIndex(
        wi => wi.id === res.payload!.workInstruction.id,
      )
      const workQueueId = res.payload?.workInstruction.workQueueId
      const workQueueDictionary = workQueueId ? this.workQueues[workQueueId] : undefined

      if (workInstructionIndex >= 0) {
        workInstructions = this.workInstructions.map((wi, index) => {
          return index === workInstructionIndex
            ? mapWorkInstructionUpsertedEventToViewModel(
                res.payload!,
                workQueueDictionary?.workQueue,
                workQueueDictionary?.craneName,
                wi.jobs,
              )
            : wi
        })
        hasUpdatedWorkInstruction = true
      } else if (
        res.payload!.workInstruction.carrierVisitId === this.selectedVisitId ||
        (this.selectedTabType === 'internal' &&
          res.payload!.workInstruction.operationType === OperationType.Internal) ||
        (this.selectedTabType === 'truck' &&
          res.payload!.workInstruction.carrierVisitType === CarrierType.Truck)
      ) {
        workInstructions = [
          ...this.workInstructions,
          mapWorkInstructionUpsertedEventToViewModel(
            res.payload!,
            workQueueDictionary?.workQueue,
            workQueueDictionary?.craneName,
          ),
        ]
        hasUpdatedWorkInstruction = true
      }

      if (hasUpdatedWorkInstruction)
        runInAction(() => {
          this.workInstructions = workInstructions
        })

      // Refresh plan progress when an internal WI lands or changes status — the m/n
      // indicator is sourced from yard, and operations doesn't push plan-level events.
      if (
        this.hasHousekeepingPanel &&
        res.payload!.workInstruction.operationType === OperationType.Internal
      ) {
        this.fetchAndSetHousekeepingPlanSummaries()
      }
    })
  }

  private handleJobUpsertedEvent = async (res: IEvent<JobUpsertedUpdatedEventPayload>) => {
    if (!res.payload) return

    const workInstructionIndex = this.workInstructions.findIndex(
      wi => wi.id === res.payload!.job.workInstructionId,
    )
    const canAdd =
      res.payload!.job.carrierVisit?.id === this.selectedVisitId ||
      (this.selectedTabType === 'internal' &&
        res.payload!.job.operationType === OperationType.Internal) ||
      (this.selectedTabType === 'truck' &&
        res.payload!.job.carrierVisit?.type === CarrierType.Truck &&
        res.payload!.job.carrierVisit.ata)

    if (workInstructionIndex < 0 && !canAdd) return

    this.upsertWorkInstructionsByIds([res.payload!.job.workInstructionId])
  }

  private handleWorkInstructionFinishedEvent = async (res: IEvent<number>) => {
    const workInstructionIndex = this.workInstructions.findIndex(wi => wi.id === res.payload)
    if (workInstructionIndex >= 0) {
      const finishedWi = this.workInstructions[workInstructionIndex]
      runInAction(() => {
        this.workInstructions = this.workInstructions.map((wi, index) =>
          index === workInstructionIndex ? { ...wi, status: WorkInstructionStatus.Finished } : wi,
        )
      })

      if (this.hasHousekeepingPanel && finishedWi.housekeepingPlanId) {
        this.fetchAndSetHousekeepingPlanSummaries()
      }
    }
  }

  private handleWorkInstructionDeletedEvent = async (
    res: IEvent<WorkInstructionDeletedEventPayload>,
  ) => {
    const deletedIds = res.payload?.ids ?? []
    const removed = this.workInstructions.filter(wi => deletedIds.includes(wi.id))
    if (removed.length === 0) return

    const removedHousekeepingPlanIds = new Set(
      removed.map(wi => wi.housekeepingPlanId).filter((id): id is string => !!id),
    )

    runInAction(() => {
      this.workInstructions = this.workInstructions.filter(wi => !deletedIds.includes(wi.id))

      // Selected group's WIs are all gone — drop the selection so it doesn't outlive its group.
      if (
        this.selectedHousekeepingGroupId &&
        removedHousekeepingPlanIds.has(this.selectedHousekeepingGroupId) &&
        !this.workInstructions.some(
          wi => wi.housekeepingPlanId === this.selectedHousekeepingGroupId,
        )
      ) {
        this.selectedHousekeepingGroupId = undefined
      }
    })

    // Refresh the left-side group list when an internal WI deletion may have emptied a plan.
    if (this.hasHousekeepingPanel && removedHousekeepingPlanIds.size > 0) {
      this.fetchAndSetHousekeepingPlanSummaries()
    }
  }

  private handleEquipmentUpsertedEvent = async (res: IEvent<EquipmentDto>) => {
    if (!res.payload) return

    if (res.payload!.equipmentType === EquipmentType.Sts) {
      this.updateCarrierVisitCraneName(res.payload.id, res.payload.name)
    }

    this.updateJobEquipmentName(res.payload.id, res.payload.name)
  }

  private handleCarrierVisitRefreshEvent = async (
    event: IEvent<CarrierVisitRefreshEventPayload>,
  ) => {
    if (!event.payload) return

    const ids =
      event
        .payload!.carrierVisits?.filter(cv => cv.carrierType !== CarrierType.Truck)
        .map(cv => cv.carrierVisitId) || []
    await this.refreshCarrierVisitsByIds(
      ids,
      event.payload!.carrierVisits?.some(cv => cv.carrierType === CarrierType.Truck),
    )
  }

  private handleCarrierVisitWorkInstructionUpsertedEvent = async (
    event: IEvent<CarrierVisitWorkInstructionUpsertedPayload>,
  ) => {
    if (!event.payload) return

    if (this.selectedVisitId === event.payload?.carrierVisitId) {
      await this.upsertWorkInstructionsByIds(event.payload!.workInstructionIds)
    }
  }

  private handleCraneSplitGeneratedForVisitEvent = async (event: IEvent<number>) => {
    if (!event.payload) return

    const ids = this.carrierVisitsWithWorkQueues.find(cv => cv.id === event.payload)
      ? [event.payload!]
      : []
    await this.refreshCarrierVisitsByIds(ids)
  }

  private upsertWorkQueueBySignalR = async (res: IEvent<WorkQueueUpsertedEventPayload>) => {
    const wqPayload = res.payload
    if (!wqPayload) return

    for (const visit of this.carrierVisitsWithWorkQueues) {
      for (const crane of visit.cranes) {
        const workQueue = crane.workQueues.find(wq => wq.id === wqPayload.id)
        if (workQueue) {
          workQueue.name = wqPayload.name
          return
        }
      }
    }

    const allWorkQueues = this.carrierVisitsWithWorkQueues.flatMap(visit =>
      visit.cranes.flatMap(crane => crane.workQueues),
    )

    const workQueue = allWorkQueues.find(wq => wq.id === wqPayload.id)
    if (workQueue) {
      runInAction(() => {
        workQueue.name = wqPayload.name
      })
    }
  }

  private refreshCarrierVisitsByIds = async (ids: number[], hasTruckVisits = false) => {
    if (!ids.length) {
      await this.fetchWorkInstructionsByCarrierVisitIdsOrTruck(ids, hasTruckVisits)
      return
    }

    const carrierVisits = await this.fetchCarrierVisits(ids)
    if (!carrierVisits) return

    const updatedCarrierVisits = [
      ...this.carrierVisitsWithWorkQueues.filter(existingVisit => !ids.includes(existingVisit.id)),
      ...carrierVisits,
    ]

    runInAction(() => {
      this.carrierVisitsWithWorkQueues = updatedCarrierVisits
    })

    if (!updatedCarrierVisits.some(x => x.id === this.selectedVisitId)) {
      await this.setFirstVisitIdForSelectedTab()
    } else {
      await this.fetchWorkInstructionsByCarrierVisitIdsOrTruck(
        carrierVisits.map(cv => cv.id),
        hasTruckVisits,
      )
    }
  }

  private fetchWorkInstructionsByCarrierVisitIdsOrTruck = async (
    carrierVisitIds: number[],
    hasTruckVisits: boolean,
  ) => {
    if (
      (this.selectedVisitId && carrierVisitIds.includes(this.selectedVisitId)) ||
      (hasTruckVisits && this.selectedTabType === 'truck')
    ) {
      await this.fetchAndSetWorkInstructions()
    }
  }

  private upsertWorkInstructionsByIds = async (ids: number[]) => {
    if (!ids.length) return

    const upsertedWorkInstructions = await this.fetchWorkInstructions(ids)
    if (!upsertedWorkInstructions || upsertedWorkInstructions.length === 0) return
    const updatedWorkInstructions = [...this.workInstructions]

    for (const upsertedWI of upsertedWorkInstructions) {
      const workQueueDictionary = upsertedWI.workQueueId
        ? this.workQueues[upsertedWI.workQueueId]
        : undefined

      const mappedWorkInstruction = mapWorkInstructionToViewModel(
        upsertedWI,
        workQueueDictionary?.workQueue,
        workQueueDictionary?.craneName,
      )

      const existingIndex = updatedWorkInstructions.findIndex(wi => wi.id === upsertedWI.id)
      if (existingIndex >= 0) {
        updatedWorkInstructions[existingIndex] = mappedWorkInstruction
      } else {
        updatedWorkInstructions.push(mappedWorkInstruction)
      }
    }

    runInAction(() => {
      this.workInstructions = updatedWorkInstructions
    })
  }

  private updateJobEquipmentName(equipmentId: number, equipmentName: string) {
    let workInstructionsAreUpdated = false
    const workInstructions: WorkInstructionControlRoomDto[] = []

    for (const wi of this.workInstructions) {
      if (wi.jobs.some(job => job.equipmentId === equipmentId)) {
        const updatedJobs = wi.jobs?.map(job =>
          job.equipmentId === equipmentId ? { ...job, equipmentName: equipmentName } : job,
        )
        workInstructions.push({ ...wi, jobs: updatedJobs })
        workInstructionsAreUpdated = true
      } else {
        workInstructions.push(wi)
      }
    }

    if (workInstructionsAreUpdated) {
      runInAction(() => {
        this.workInstructions = workInstructions
      })
    }
  }

  private updateCarrierVisitCraneName(equipmentId: number, craneName: string) {
    let carrierVisitsAreUpdated = false
    const updatedCarrierVisits: CarrierVisitWithWorkQueueDto[] = []

    for (const visit of this.carrierVisitsWithWorkQueues) {
      if (visit.cranes.some(crane => equipmentId === crane.id)) {
        updatedCarrierVisits.push({
          ...visit,
          cranes: visit.cranes.map(crane =>
            crane.id === equipmentId ? { ...crane, name: craneName } : crane,
          ),
        })
        carrierVisitsAreUpdated = true
      } else {
        updatedCarrierVisits.push(visit)
      }
    }

    if (carrierVisitsAreUpdated) {
      runInAction(() => {
        this.carrierVisitsWithWorkQueues = updatedCarrierVisits
      })
    }
  }

  private enqueueSignalREvent = async (eventMessage: any, eventHandler: any) => {
    this.signalREventsQueue.push({
      eventMessage,
      eventHandler,
    })

    if (this.signalREventsQueue.length === 1) {
      appStore.forbidLoader()

      await this.processSignalRQueue().finally(() => appStore.unforbidLoader())
    }
  }

  private processSignalRQueue = async () => {
    if (!this.signalREventsQueue.length) {
      return
    }

    const firstEvent = this.signalREventsQueue[0]
    await firstEvent.eventHandler(firstEvent.eventMessage)

    this.signalREventsQueue.shift()

    await this.processSignalRQueue()
  }
}
